<template>
  <div id="billing" class="container-24">
    <loading
      :show="showSpinner"
      label="Loading..."
      :overlay="true">
    </loading>

    <div class="container billing-container">
      <div class="row billing-section">
        <section class="col-sm-12 billing-desc-header">
          <h3 class="title-24">Your Billing History</h3>
          <section class="card-24">
            <div class="card-text-container col-sm-12">
              <div class="text-24">
                <p>
                  View past charges and payments, as well as any unpaid balance on your account.
                </p>
              </div>
            </div>
          </section>
        </section><!-- billing description -->
      </div>
    </div>

    <div class="container billing-container">
      <div class="row billing-section">
        <div class="col-md-12">
          <div class="row edit-form">
            <div class="col-lg-10 col-md-10 col-sm-12 billing-about">
              <div class="row">
                <div class="billing-about-section materialized col-lg-6 col-md-6 col-sm-12 col-xs-12 ">
                  <div class="form-group materialized">
                    <multi-select v-model="billing.memberFaoNames" :options="allMembers" :multiple="true" :searchable="false"
                                  label="memberFullName" id="billing-members" :disabled="allMembers.length < 2"
                                  placeholder="Select" track-by="memberNumber" :limit="0" :limit-text="limitText"
                                  deselectLabel="Remove" selectLabel="Add" :allowEmpty="false" :closeOnSelect="false">
                    </multi-select>
                    <label for="billing-members">Select Members</label>
                  </div>
                </div>
                <div class="billing-about-section materialized col-lg-5 col-md-6 col-sm-12 col-xs-12">
                  <div class="form-group materialized">
                    <date-range
                      v-on:input="dateRangeChanged"
                      :initialStartDate="testNewDate()"
                      :initialEndDate="new Date()"></date-range>
                    <label for="billing-period">Select Period</label>
                    <input type="button" class="btn btn-primary date-range-go" v-on:click="searchByDateRangeUi(false)"
                           value="GO"
                           :disabled="dateErrors.length > 0">
                    <span v-show="dateErrors.length > 0"
                          class="general-error-text">{{ dateErrors[0] }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-1 col-md-1 col-sm-2 col-xs-12 hidden-xs hidden-sm pull-right materialized">
              <button id="billing-print" v-on:click="printBilling()" class="btn btn-default btn-print" :disabled="isPrinting || filteredRows.length === 0">
                <i class="fa fa-cog fa-spin fa-fw margin-bottom security-saving-icon"
                   v-show="isPrinting"></i>
                <i class="fa fa-print"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="container billing-container">
      <div class="row billing-section">
        <div id="billing-history-table" class="col-sm-12 materialized">
          <vue-good-table
            :columns="columns"
            :rows="filteredRows"
            :paginate="true"
            :rowsPerPageText="billing.rowsPerPageText"
            :showPageNumberForMobile="true"
            :showFootText="true"
            :footLabel="tfooter.label"
            :footValue="totalValue"
            :nextText="billing.nextText"
            :prevText="billing.prevText"
            styleClass="table condensed table-hover">
            <template slot="table-row" slot-scope="props">
              <td class="text-left-bh"><span class="data-item">
                <!-- {{props.row.memberNumber === memberNumber ? '★ ' : ''}} -->
                <i v-if="props.row.memberNumber === memberNumber" class="fa fa-star"></i>
                {{ props.row.memberName }}
              </span></td>
              <td><span class="data-item">{{ props.row.date }}</span></td>
              <td class="text-center-bh hidden-xs-bh"><span class="data-item">{{ props.row.agreement }}</span></td>
              <td class="text-center hidden-xs-bh"><span class="data-item">{{ props.row.referenceNumber}}</span></td>
              <td class="text-center-bh"><span class="data-item" v-bind:class="{'payment-done-color': props.row.numericBalance < 0}">{{props.row.description}}</span>
              </td>
              <td class="text-right-bh">
                <span class="data-item brand-24-green"
                      v-bind:class="{'payment-done-color': props.row.numericBalance < 0}">{{ '$' + props.row.numericBalance.toFixed(2)
                  }}</span>
              </td>
              <td class="hidden-sm-bh">
                <span v-if="props.row.numericBalance < 0">Paid</span>
                <span v-if="props.row.numericBalance > 0">Unpaid</span>
              </td>
            </template>
            <template slot="table-under-banner">
              <alert-banner v-if="filteredRows.length === 0 && !showSpinner"
                            :alertMessage="'There is no account activity for the date range provided.'">
              </alert-banner>
            </template>
          </vue-good-table>

          <div class="table-footer clearfix bh-table-footer bh-table-mobile-footer">
            <div>
              <span class="tfoot-text-bh">Unpaid Balance:</span>
              <span class="pull-right tfoot-text-bh">{{totalValue}}</span>
            </div>
          </div>

          <div class="primary-member-legend">
            <i class="fa fa-star"></i> Primary Member
          </div>
        </div>
      </div>
    </div>

    <div class="container billing-container">
      <div class="row billing-section bottom-cta-button">
        <div class="col-md-12 materialized make-payment-container">
          <router-link :to="{name: 'MakeAPayment'}">
            <button id="billing-payment" class="btn btn-primary pull-right">Make a Payment</button>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {AlertBanner} from 'my-account-common-vue'
  import {ServiceContainer, dateRange, loading} from 'CommonBilling'
  import Multiselect from 'vue-multiselect'
  import 'vue-multiselect/dist/vue-multiselect.min.css'
  import _ from 'lodash'
  import {StringUtils} from 'my-account-common'
  import FileSaver from 'file-saver'

  const billingService = ServiceContainer.billingService

  export default {
    name: 'billing',
    components: {
      'date-range': dateRange,
      'loading': loading,
      'multi-select': Multiselect,
      'alert-banner': AlertBanner
    },
    data() {
      return {
        timeZone: (function () {
          let date = new Date()
          return (-(date.getTimezoneOffset()))
        })(),
        showSpinner: false,
        isPrinting: false,
        dateErrors: [],
        datePickerStartDate: (function () {
          let start = new Date()
          start.setMonth(start.getMonth() - 6)
          return start
        })(),
        datePickerEndDate: new Date(),
        memberNumber: '',
        memberName: '',
        fooBar: '',
        columns: [
          {
            label: 'Member',
            field: 'memberName',
            sortable: true
          },
          {
            label: 'Date',
            field: 'date',
            type: 'date',
            tdClass: 'text-center',
            thClass: 'text-center',
            html: '<span class="title" data-title="Date"></span>',
            sortable: true
          },
          {
            label: 'Agreement',
            field: 'agreement',
            tdClass: 'text-center',
            thClass: 'text-center',
            sortable: true
          },
          {
            label: 'Reference Number',
            field: 'referenceNumber',
            tdClass: 'text-center',
            thClass: 'text-center',
            sortable: true
          },
          {
            label: 'Description',
            field: 'description',
            tdClass: 'text-center',
            thClass: 'text-center',
            sortable: true
          },
          {
            label: 'Balance',
            field: 'numericBalance',
            type: 'decimal',
            tdClass: 'text-center',
            thClass: 'text-center',
            sortable: true
          }],
        rows: [],
        tfooter: {
          label: 'Total Balance:',
          value: '0.00'
        },
        billing: {
          memberFaoNames: [],
          masterMember: {},
          memberFaos: [],
          rowsPerPageText: 'Rows',
          nextText: '',
          prevText: ''
        }
      }
    },
    mounted() {
      this.showSpinner = true
      this.executeFirstRequest().then(() => {
        this.showSpinner = false
        this.billing.memberFaoNames = this.billing.memberFaos.concat({
          'memberFullName': this.memberName,
          'memberNumber': this.memberNumber
        })
        this.billing.memberFaoNames = _.map(this.billing.memberFaoNames, (row) => {
          row.memberFullName = StringUtils.initCaps(row.memberFullName)
          return row
        })
      }).catch((error) => {
        this.showSpinner = false
        if (error.response !== undefined && error.response.data.errorMessage !== undefined) {
          this.$toasted.show('<p>' + error.response.data.errorMessage + '</p>', {
            position: 'bottom-center',
            duration: 10000
          })
        }
      })
    },
    methods: {
      testNewDate() {
        if (typeof this.datePickerStartDate.getMonth === 'function') {
          return this.datePickerStartDate
        }
        let start = new Date()
        start.setMonth(start.getMonth() - 5)
        return start
      },
      executeFirstRequest() {
        return new Promise((resolve, reject) => {
          this.callSearchByDate(true).then((response) => {
            this.setData(response.data)
            resolve()
          }).catch((error) => {
            if (error.response !== undefined && error.response.status === 412) {
              this.datePickerStartDate = new Date(error.response.data.enrollmentDate)
              this.callSearchByDate(true)
                .then((response) => {
                  this.setData(response.data)
                  resolve()
                })
                .catch((secondError) => {
                  reject(secondError)
                })
            } else {
              reject(error)
            }
          })
        })
      },
      limitText(count) {
        if (count === 1) {
          let start = this.billing.memberFaoNames[0].memberNumber === this.memberNumber ? '★ ' : ''
          start += this.billing.memberFaoNames[0].memberFullName
          return start
        }
        return 'Selected (' + count + ')'
      },
      searchByDateRangeUi(withCache) {
        this.$nextTick(function () {
          this.searchByDateRange(withCache)
        })
      },
      searchByDateRange(withCache) {
        return new Promise((resolve, reject) => {
          this.showSpinner = true
          this.callSearchByDate(withCache).then((response) => {
            this.showSpinner = false
            this.setData(response.data)
            resolve(response)
          }).catch((error) => {
            this.showSpinner = false
            if (error.response !== undefined && error.response.data.errorMessage !== undefined) {
              this.$snackbar.create(error.response.data.errorMessage, {}, function () {
              })
            }
            reject(error)
          })
        })
      },
      callSearchByDate(withCache) {
        return billingService.getBillingHistoryView({
          useCache: withCache,
          ttl: 10,
          params: {
            startDate: this.formatDate(this.datePickerStartDate),
            endDate: this.formatDate(this.datePickerEndDate),
            timezoneOffset: this.timeZone
          }
        })
      },
      /**
       * @param {Object} event data from data-rage component
       * @return void
       */
      dateRangeChanged(event) {
        this.datePickerStartDate = event.datePickerStartDate
        this.datePickerEndDate = event.datePickerEndDate
        this.dateErrors = event.errors
      },
      formatDate(date) {
        const day = date.getDate()
        const monthIndex = date.getMonth()
        const year = date.getFullYear()
        return (monthIndex + 1) + '/' + day + '/' + year
      },
      printBilling() {
        let memberWithAgreements = _.filter(this.billing.memberFaoNames, (member) => {
          return (_.some(this.rows, (row) => row.memberNumber === member.memberNumber))
        })
        if (memberWithAgreements.length === 0) {
          this.$snackbar.create('No members with agreements were selected ', {}, function () {
          })
          return
        }
        this.isPrinting = true
        let promises = _.map(memberWithAgreements, (member) => {
          return billingService.printBillingHistory({
            responseType: 'blob',
            params: {
              startDate: this.formatDate(this.datePickerStartDate),
              endDate: this.formatDate(this.datePickerEndDate),
              memberNumber: member.memberNumber,
              timezoneOffset: this.timeZone
            }
          }).then((success) => {
            this.openFile(success, member.memberNumber)
          }).catch((error) => {
            if (error.response !== undefined && error.response.data.errorMessage !== undefined) {
              this.$snackbar.create(error.response.data.errorMessage, {}, function () {
              })
            } else {
              this.$snackbar.create('Could not print pdf', {}, function () {
              })
            }
          })
        })
        Promise.all(promises).then(() => {
          this.isPrinting = false
        }).catch(() => {
          this.isPrinting = false
        })
      },
      openFile(success, member) {
        let blob = success.data
        FileSaver.saveAs(blob, member + '-' + this.formatDateForFile(new Date()) + '.pdf')
      },
      formatDateForFile(date) {
        const day = (date.getDate() < 10) ? '0' + date.getDate() : date.getDate()
        const monthIndex = (date.getMonth() + 1 < 10) ? '0' + (date.getMonth() + 1) : date.getMonth() + 1
        const year = date.getFullYear()
        return year + '' + monthIndex + '' + day
      },
      /**
       * Sets the information for view billing from the <<billingService>
       * @param {Object} viewData
       * @return {Null}
       */
      setData(viewData) {
        this.rows = viewData.billingList
        this.billing.memberFaos = viewData.faos
        this.memberNumber = viewData.memberNumber
        this.memberName = StringUtils.initCaps(viewData.memberName)
      }
    },
    computed: {
      filteredRows: {
        get: function () {
          let filterResult = _.filter(this.rows, (row) => {
            return _.includes(_.map(this.billing.memberFaoNames, 'memberNumber'), row.memberNumber)
          })

          return _.map(filterResult, (row) => {
            const newRow = _.cloneDeep(row)
            newRow.memberName = StringUtils.initCaps(newRow.memberName)
            newRow.numericBalance = newRow.balance
            newRow.balance = '$' + newRow.balance
            return newRow
          })
        }
      },
      totalValue: {
        get: function () {
          return '$' + _.sumBy(_.filter(this.filteredRows, (o) => { return  o.description.toLowerCase() !== 'refund'}), 'numericBalance').toFixed(2)
        }
      },
      allMembers: {
        get: function () {
          const selectableMembers = this.billing.memberFaos.slice(0)
          return selectableMembers.concat({memberNumber: this.memberNumber, memberFullName: this.memberName})
        }
      },
      selectAll: {
        get: function () {
          return this.billing.memberFaos ? this.selected.length === _.size(this.billing.memberFaos) : false
        },
        set: function (value) {
          let selected = []

          if (value) {
            _.forEach(this.billing.memberFaos, function (memberFao, key) {
              selected.push(memberFao.memberNumber)
            })
          }
          this.selected = selected
        }
      }
    }
  }
</script>

<style lang="scss">
  @import '~CommonBilling/css/variables';

  // ADA multiselect fix
  ul.multiselect__content {
    li.multiselect__element {
      .multiselect__option--highlight {
        background: $md-brand-green;
        &::after {
          background: $md-brand-green;
        }
      }
      .multiselect__option--selected {
        &.multiselect__option--highlight {
          background: $md-brand-red;
          &::after {
            background: $md-brand-red;
          }
        }
      }
    }
  }

  i.fa.fa-star {
    font-size: .75em;
    vertical-align: super;
    top: 4px;
    position: relative;
  }

  #not-max-fao {
    padding: 20px;
  }

  .billing-hide-btn {
    display: none;
  }

  .billing-cancel-btn {
    margin-left: 10px;
  }

  .billing-saving-icon {
    font-size: 19px;
  }

  .billing-container .edit-form {
    padding: 10px 15px;
    background-color: #f5f5f5;
    margin: 0;
  }

  .datatable-length {
    margin-left: 10px;
  }

  .btn-print {
    margin: 14px 0;
  }

  #billing-history-table {
    .tfoot-text-bh {
      font-weight: bold;
    }
  }

  .table-footer {
    margin-bottom: 0px !important;
  }

  .primary-member-legend {
    color: #9a9a9a;
    padding: 15px 0 15px 15px;
    border-bottom: 1px solid #dddddd;
  }

  .payment-done-color {
    color: $md-brand-green;
  }

  .make-payment-container {
    margin: 10px 0px 20px 0px;
  }

  /* Small devices (phones under 768px) */
  @media (max-width: $screen-sm-min) {

    .date-range-go {
      margin-left: 5px;
    }

    .billing-image-src {
      float: left !important;
      width: 100%;
      display: none;
    }

    .billing-list-group {
      list-style: none;
      padding: 5px 0 5px 0;
      li {
        border-bottom: 1px solid #D5D2CA;
        border-bottom: 1px solid #D5D2CA;
        padding: 4px 0 4px 0;
        span {
          margin-left: 10px;
        }
      }
    }
    .billing-print {
      display: none;
    }
    .date-range-go {
      margin-top: 3%;
      float: right;
    }

    .bh-table-mobile-footer {
      display: block;
    }

    .tfoot-text {
      display: none;
    }

    .bh-hide-mobile {
      display: none !important;
    }

    .materialized .good-table .responsive table.table tr {
      margin-top: 10px !important;
      margin-bottom: 10px !important;
    }

    .materialized .good-table .responsive table.table td {
      padding: 2px 10px !important;
    }

    materialized .good-table .responsive table.table tr {
      margin: 5px 0px 5px 0px !important;
    }
  }

  .billing-disable-link {
    cursor: not-allowed;
    backgroud-color: #F6F6F6 !important;
  }

  .validationError.is-danger {
    border-color: #ff3860;
  }

  /* Small devices (tablets, 768px and up) */
  @media (min-width: $screen-sm-min) {

    .billing-image-src {
      float: left !important;
      width: 100%;
      display: none;
    }

    .billing-list-group {
      list-style: none;
      padding: 5px 0 5px 0;
      li {
        border-bottom: 1px solid #D5D2CA;
        border-bottom: 1px solid #D5D2CA;
        padding: 10px 0;
        span {
          margin-left: 10px;
        }
      }
    }

    .date-range-go {
      margin-top: 3%;
      float: right;
    }

    .bh-table-mobile-footer {
      display: none;
    }

    .tfoot-text {
      display: block;
    }
  }

  /* Medium devices (desktops, 992px and up) */
  @media (min-width: $screen-md-min) {

    .billing-image-src {
      float: left !important;
      width: 100%;
      display: block;
    }

    .date-range-go {
      margin-top: 0%;
      margin-left: -30%;
      float: inherit;
    }

    .billing-list-group {
      list-style: none;
      padding: 5px 0 5px 0;
      li {
        border-bottom: 1px solid #D5D2CA;
        padding: 10px 0;
        span {
          margin-left: 10px;
        }
      }
    }

    .bh-table-mobile-footer {
      display: none;
    }

    .tfoot-text {
      display: block;
    }
  }

  /* Large devices (large desktops, 1200px and up) */
  @media (min-width: $screen-lg-min) {
    .date-range-go {
      margin-top: 0%;
      margin-left: -19%;
      float: inherit;
    }

    .bh-table-mobile-footer {
      display: none;
    }

    .tfoot-text {
      display: block;
    }
  }

  @media only screen and (min-width: 600px) {
    table {
      tr {
        td {
          &.text-left-bh {
            text-align: left !important;
          }
          &.text-center-bh {
            text-align: center !important;
          }
          &.text-right-bh {
            text-align: right !important;
          }
        }
      }
    }
  }

  @media only screen and (max-width: 600px) {
    .payment-done-color {
      color: 000;
    }
  }

  @media only screen and (max-width: 750px) {
    .controls-top, .controls-bottom {
      .info {
        display: inline;
      }
    }
  }

  .materialized .good-table .table-footer.clearfix {
    margin-bottom: 0px;
    div.pull-right span.tfoot-text-bh {
      font-weight: bold;
    }
  }

  .bh-table-footer {
    background-color: #44697D;
    color: #ffffff;
    padding: 15px 20px;
    border-radius: 0;
    border: 0;
    font-weight: bold;
  }
</style>



// WEBPACK FOOTER //
// node_modules/BillingHistory/Billing.vue